import { DbRef, OrderStatus, ProcedureStatus, OrderOperationType } from '@caresend/types';
import { getBookingPath } from './booking.js';
import { getPath } from './path.js';
import { getProcedurePath } from './procedure.js';
import { getUserPath } from './user.js';
import { encryptBooking } from '../encryption/booking.js';
import { getValueOnce } from '../environment/data/privateHelpers.js';
import { getValidatedUnencryptedBooking } from '../typeValidation/booking.js';
import { getValidatedDraftOrder } from '../typeValidation/draftOrder.js';
import '../typeValidation/draftWaypoint.js';
import { objectMap } from '../languageExtensions/object.js';
import 'lodash-unified';
import '../typeValidation/feedback.js';
import '../environment/crypto/index.js';
import { getCurrentUtcIsoDateTime } from '../initializers/date.js';
import { deduplicateArrayByProperty } from '../languageExtensions/array.js';
import 'dayjs';
import '../formatters/string.js';
import 'ngeohash';
import '../formatters/location.js';
import 'immutability-helper';
import '../initializers/user.js';
import '../typeValidation/note.js';
import { getValidatedOrder } from '../typeValidation/order.js';
import { getValidatedProcedure } from '../typeValidation/procedure.js';
import '../typeValidation/samples.js';
import '../typeValidation/shift.js';
import '../typeValidation/shipmentInfo.js';
import '../typeValidation/supply.js';
import '../typeValidation/timeSlot.js';
import '../typeValidation/waypoint.js';
import '../typeValidation/waypointAction.js';
import 'lodash.clonedeep';
import { encryptOrder } from '../encryption/order.js';
import { encryptProcedure } from '../encryption/procedure.js';

/**
 * ```
 * `${DbRef.ORDERS}/${id}/${childPath}`
 * ```
 */

function getOrderPath(id, childPath) {
  return getPath(`${DbRef.ORDERS}/${id}`, childPath);
}

const getOrderWithUpdatedOperations = (order, source) => {
  var _a, _b, _c;

  const {
    operations
  } = order;
  const type = (operations === null || operations === void 0 ? void 0 : operations.create) ? OrderOperationType.UPDATE : OrderOperationType.CREATE;
  const dateTime = getCurrentUtcIsoDateTime();

  if (type === OrderOperationType.CREATE) {
    return Object.assign(Object.assign({}, order), {
      operations: {
        create: {
          [source]: {
            dateTime
          }
        }
      }
    });
  }

  const existingUpdates = (_b = (_a = order.operations) === null || _a === void 0 ? void 0 : _a.update) !== null && _b !== void 0 ? _b : {};
  const existingUpdatesForSource = (_c = existingUpdates[source]) !== null && _c !== void 0 ? _c : {};
  const newUpdateOperations = Object.assign(Object.assign({}, existingUpdates), {
    [source]: Object.assign(Object.assign({}, existingUpdatesForSource), {
      [dateTime]: {
        dateTime
      }
    })
  });
  return Object.assign(Object.assign({}, order), {
    operations: Object.assign(Object.assign({}, order.operations), {
      update: newUpdateOperations
    })
  });
};

const getUpdatesForDraftOrder = async ({
  bookings,
  order,
  patientInitiated,
  procedures,
  queuedForScheduling,
  readyForScheduling,
  schedulingFlowDraftWaypointID,
  source
}) => {
  const orderWithUpdatedOperations = getOrderWithUpdatedOperations(order, source);
  const validatedDraftOrder = getValidatedDraftOrder(orderWithUpdatedOperations);

  if (readyForScheduling) {
    validatedDraftOrder.readyForScheduling = readyForScheduling;
  }

  if (queuedForScheduling) {
    validatedDraftOrder.queuedForScheduling = queuedForScheduling;
  }

  if (patientInitiated && schedulingFlowDraftWaypointID) {
    validatedDraftOrder.draftWaypoints = {
      [schedulingFlowDraftWaypointID]: true
    };
  }

  const updates = {};
  const bookingPromises = Object.values(bookings !== null && bookings !== void 0 ? bookings : {}).map(async booking => {
    const encryptedBooking = await encryptBooking(booking);
    updates[getBookingPath(booking.id)] = encryptedBooking;
  });
  const procedurePromises = Object.values(procedures !== null && procedures !== void 0 ? procedures : {}).map(async procedure => {
    const encryptedProcedure = await encryptProcedure(procedure);
    updates[getProcedurePath(procedure.id)] = encryptedProcedure;
  });
  await Promise.all([...bookingPromises, ...procedurePromises]);
  const encryptedOrder = await encryptOrder(validatedDraftOrder);
  updates[getOrderPath(encryptedOrder.id)] = encryptedOrder;
  return updates;
};
/**
 * Returns updates required to create or update a full order (status `created`)
 */

const getUpdatesForFullOrder = async ({
  bookings,
  order,
  procedures,
  source
}, creator) => {
  const orderWithUpdatedOperations = getOrderWithUpdatedOperations(order, source);
  const validatedOrder = Object.assign(Object.assign({}, getValidatedOrder(orderWithUpdatedOperations)), {
    status: OrderStatus.CREATED
  });
  const validatedBookings = objectMap(bookings, getValidatedUnencryptedBooking);
  const validatedProcedures = objectMap(procedures, getValidatedProcedure);
  const updates = {};
  const bookingPromises = Object.values(validatedBookings).map(async booking => {
    const encryptedBooking = await encryptBooking(booking);
    updates[getBookingPath(booking.id)] = encryptedBooking;
  });
  const procedurePromises = Object.values(validatedProcedures).map(async procedure => {
    var _a, _b;

    if (procedure.diagnoses) {
      const patientID = (_a = validatedBookings === null || validatedBookings === void 0 ? void 0 : validatedBookings[procedure.bookingID]) === null || _a === void 0 ? void 0 : _a.patientID;
      if (!patientID) return;
      const patient = await getValueOnce(getUserPath(patientID));

      if (patient) {
        const existingDiagnoses = (_b = patient === null || patient === void 0 ? void 0 : patient.diagnoses) !== null && _b !== void 0 ? _b : [];
        const updatedDiagnoses = existingDiagnoses.concat(procedure.diagnoses);
        const updatedDiagnosesWithoutDuplicates = deduplicateArrayByProperty(updatedDiagnoses, 'secretID');
        updates[getUserPath(patient.id, 'diagnoses')] = updatedDiagnosesWithoutDuplicates;
      }
    }

    const encryptedProcedure = await encryptProcedure(procedure);
    encryptedProcedure.status = ProcedureStatus.CREATED;
    updates[getProcedurePath(procedure.id)] = encryptedProcedure;
  });
  await Promise.all([...bookingPromises, ...procedurePromises]);
  const encryptedOrder = await encryptOrder(validatedOrder, creator);
  updates[getOrderPath(order.id)] = encryptedOrder;
  return updates;
};

export { getOrderPath, getUpdatesForDraftOrder, getUpdatesForFullOrder };
